import {complement, equals, map, pick, prop, propEq} from 'ramda';
import {GenericItemImage} from './Common';
import Constants, {internalToClientResaleType, ResaleType} from './Constants';
import {ItemDescription} from './ItemDescriptions';
import {FlatPickup} from './Pickups';
import {addDays, isAfter, isoNow} from './Time';
import {TruckItem} from './TruckItems';
const {
  ResaleType: {
    AUCTION,
    CONSIGNMENT,
    DONATION,
    DONATION_TIER_2,
    RECYCLE,
    UNSURE,
    REJECTED,
    FLASH_SALE,
  },
  ItemStatus,
} = Constants;

const PAID = 'Paid';
const SOLD = 'Sold';
const PENDING_SALE = 'Pending Sale';
const CLAIMED = 'Claimed';
const DONATED = 'Donated';
const FREE = 'Free';
const RECYCLED = 'Recycled';
const RETURNED = 'Returned';
const FOR_SALE = 'For Sale';
const PROCESSING = 'Processing';
const AUCTIONING = 'Auctioning';
const AUCTIONED = 'Auctioned';
export const CustomerItemStatus = {
  PAID,
  SOLD,
  PENDING_SALE,
  CLAIMED,
  DONATED,
  FREE,
  RECYCLED,
  RETURNED,
  FOR_SALE,
  PROCESSING,
  AUCTIONING,
  AUCTIONED,
} as const;
export const CustomerItemStatusDescription = {
  [PROCESSING]: 'The item is under review by our curator.',
  [FOR_SALE]: 'We continue to look for the a buyer or resale partner.',
  [PENDING_SALE]: 'Item has sold but the return window has not expired.',
  [SOLD]: 'Item sold.',
  [PAID]: 'Item has sold and payment has been sent.',
  [DONATED]: 'Item has been donated.',
  [RECYCLED]: 'Item has been responsibly disposed.',
  [RETURNED]: 'Item has been returned to customer.',
  [AUCTIONING]: 'The item is being auctioned.',
  [AUCTIONED]: 'The item was auctioned.',
};
export const CUSTOMER_ITEMS_SORT = {
  barcode_asc: 'barcode ASC',
  barcode_desc: 'barcode DESC',
  description_asc: 'description ASC',
  description_desc: 'description DESC',
  quantity_asc: 'quantity ASC',
  quantity_desc: 'quantity DESC',
  status_asc: 'statuses.status_id ASC',
  status_desc: 'statuses.status_id DESC',
  end_of_consignment_asc: 'end_of_consignment ASC',
  end_of_consignment_desc: 'end_of_consignment DESC',
};
export type CustomerItemSearch = {
  paginate?: {page: number; perPage: number} | null | undefined;
  ownerId?: number;
  pickupId?: number | null | undefined;
  sort?: string | null | undefined;
  status?: string[] | null | undefined;
  // sort?: ?$Keys<typeof CUSTOMER_ITEMS_SORT>,
  categoryIds?: Maybe<number[]>;
  resaleType?: 'Consignment' | 'Auction';
};
export type CustomerPortalItemsTab = 'consignment' | 'donation' | 'recycle' | 'expired' | 'auction';

// TODO: add flag to DB
export const BLUEGROUP_ID = 23642;
export const ZEUS_ID = 23769;
export const ZEUS_NO_SALE_ID = 30645;
export const TEMP_OWNER_ID = 23933;
export const ENTERPRISE_CUSTOMERS = [BLUEGROUP_ID, ZEUS_ID, ZEUS_NO_SALE_ID];
export const ENTERPRISE_CUSTOMER_END_OF_CONSIGNMENT = '2020-10-15T00:00:00-07:00';

export type CustomerItem = {
  type: Maybe<'ItemDescription' | 'TruckItem'>;
  id: Maybe<number>;
  barcode: Maybe<string>;
  status: Maybe<string>;
  small_url: Maybe<string>;
  big_url: Maybe<string>;
  market: Maybe<string>;
  amount: Maybe<string>;
  item_category_id: Maybe<number>;
  quantity: Maybe<number>;
  total_pieces: Maybe<string>;
  extra_pieces: Maybe<string>;
  width: Maybe<string>;
  depth: Maybe<string>;
  height: Maybe<string>;
  weight: Maybe<string>;
  estimate_resale_value: Maybe<string>;
  description: Maybe<string>;
  brand: Maybe<string>;
  original_price: Maybe<string>;
  model: Maybe<string>;
  material: Maybe<string>;
  purchase_date: Maybe<string>;
  pickup_id: Maybe<number>;
  public_notes: Maybe<string>;
  end_of_consignment: Maybe<string>;
  end_of_consignment_action: Maybe<string>;
  end_of_consignment_pickup_at: Maybe<string>;
  external_sku: Maybe<string>;
  merchant_order_item_ids: Maybe<number>[];
  image: Maybe<GenericItemImage>;
};
export const EMPTY_CUSTOMER_ITEM = {
  type: null,
  id: null,
  barcode: null,
  status: null,
  small_url: null,
  big_url: null,
  market: null,
  amount: null,
  item_category_id: null,
  quantity: null,
  total_pieces: null,
  extra_pieces: null,
  width: null,
  depth: null,
  height: null,
  weight: null,
  estimate_resale_value: null,
  description: null,
  brand: null,
  original_price: null,
  model: null,
  material: null,
  purchase_date: null,
  pickup_id: null,
  public_notes: null,
  end_of_consignment: null,
  end_of_consignment_action: null,
  end_of_consignment_pickup_at: null,
  external_sku: null,
  merchant_order_item_ids: [],
};
const stringify = (a: Maybe<number>): Maybe<string> =>
  a === null || a === undefined ? a : String(a);
export function customerDonationItems(truckItems: readonly TruckItem[]): CustomerItem[] {
  return truckItems
    .filter(complement(prop('hidden')))
    .filter(i => [DONATION, DONATION_TIER_2].includes(i.resale_type))
    .map(truckItemToCustomerItem);
}
export function customerRecycleItems(truckItems: TruckItem[]): CustomerItem[] {
  return truckItems
    .filter(complement(prop('hidden')))
    .filter(propEq('resale_type', RECYCLE))
    .map(truckItemToCustomerItem);
}
export function truckItemToCustomerItem(truckItem: TruckItem): CustomerItem {
  const {id, generic_item_images, barcode, total_quantity} = truckItem;
  return {
    ...EMPTY_CUSTOMER_ITEM,
    ...pick(Object.keys(EMPTY_CUSTOMER_ITEM), truckItem),
    ...map(
      stringify,
      pick(['width', 'height', 'depth', 'total_pieces', 'extra_pieces'], truckItem),
    ),
    barcode: stringify(barcode),
    id,
    image: generic_item_images[0],
    small_url: generic_item_images[0]?.small_url,
    big_url: generic_item_images[0]?.big_url,
    quantity: total_quantity,
    type: 'TruckItem',
    description: truckItem.item_category,
  };
}
export function itemDescriptionToCustomerItem(item: ItemDescription): CustomerItem {
  const {id, main_image, total_quantity} = item;
  return {
    ...EMPTY_CUSTOMER_ITEM,
    ...pick(Object.keys(EMPTY_CUSTOMER_ITEM), item),
    ...map(stringify, pick(['width', 'height', 'depth', 'total_pieces', 'extra_pieces'], item)),
    id,
    image: main_image,
    small_url: main_image?.small_url,
    big_url: main_image?.big_url,
    quantity: total_quantity,
    type: 'ItemDescription',
    description: item.posting_title,
    end_of_consignment: item.end_of_consignment_timestamp,
  };
}
export type ItemUnit = {
  id?: number | null | undefined;
  item_description_id: number | null | undefined;
  merchant_order_item_id: number | null | undefined;
  owner_id: number | null | undefined;
  pickup_id: number | null | undefined;
  truck_item_id: number | null | undefined;
};
export type ItemUnitSearch = {
  paginate?: {page: number; perPage: number} | null | undefined;
  item_description_id?: number[];
  truck_item_id?: number[] | null | undefined;
  merchant_order_item_id?: number[] | null | undefined;
  pickup_id?: number[] | null | undefined;
  owner_id?: number[] | null | undefined;
  id?: number[] | null | undefined;
};
export const ITEM_UNIT_FIELDS = [
  'merchant_order_item_id',
  'item_description_id',
  'truck_item_id',
  'pickup_id',
  'owner_id',
];
export function diffItemUnits(
  saved: ItemUnit[],
  calculated: ItemUnit[],
): {
  toCreate: ItemUnit[];
  toDelete: ItemUnit[];
} {
  const sameItemUnit = (a, b) =>
    a && b && equals(pick(ITEM_UNIT_FIELDS, a), pick(ITEM_UNIT_FIELDS, b));
  const toDelete = [...saved];
  let toDeleteIndex = toDelete.length;
  const toCreate = [...calculated];
  while (toDeleteIndex--) {
    let toCreateIndex = toCreate.length;
    while (toCreateIndex--) {
      if (sameItemUnit(toDelete[toDeleteIndex], toCreate[toCreateIndex])) {
        toDelete.splice(toDeleteIndex, 1);
        toCreate.splice(toCreateIndex, 1);
      }
    }
  }
  return {toCreate, toDelete};
}
export function isMyCollectionAvailable(
  counts: Record<keyof typeof CustomerItemStatus, number>,
  pickups: FlatPickup[],
): boolean {
  const thirtyDaysAgo = addDays(isoNow(), -30);
  const pickupDates = pickups.map(prop('pickup_date')).filter(Boolean);
  return (
    counts[PROCESSING] + counts[FOR_SALE] + counts[AUCTIONING] >= 4 &&
    pickupDates.some(pickupDate => isAfter(pickupDate, thirtyDaysAgo))
  );
}
const TAB_COLLECTION_RESALE_TYPE = {
  auction: AUCTION,
  consignment: CONSIGNMENT,
  donation: DONATION,
  recycle: RECYCLE,
} as const;

type CustomerPortalItemsTabWithBadge = keyof typeof TAB_COLLECTION_RESALE_TYPE;

function customerItemStatusToCurrentResaleType(
  tab: CustomerPortalItemsTabWithBadge,
  status: Maybe<string>,
): Maybe<ResaleType> {
  if (!status) return null;
  if ([FOR_SALE, PENDING_SALE, SOLD, FREE, CLAIMED].includes(status)) {
    return CONSIGNMENT;
  }
  if ([AUCTIONING, AUCTIONED].includes(status)) return AUCTION;
  if (status === PAID) {
    return tab === 'auction' ? AUCTION : CONSIGNMENT;
  }
  if (status === DONATED) return DONATION;
  if (status === RECYCLED) return RECYCLE;
  return null;
}
const RESALE_TYPE_RANK: Record<ResaleType, number> = {
  [RECYCLE]: 0,
  [DONATION]: 1,
  [AUCTION]: 2,
  [CONSIGNMENT]: 3,
  [FLASH_SALE]: 0,
  [DONATION_TIER_2]: 1,
  [UNSURE]: 0,
  [REJECTED]: 0,
};
function resaleTypeRank(resaleType: ResaleType): number {
  const clientResaleType = internalToClientResaleType(resaleType);
  return clientResaleType ? RESALE_TYPE_RANK[clientResaleType] ?? -1 : -1;
}
export function customerItemPreviousResaleTypeBadge(
  tab: CustomerPortalItemsTabWithBadge,
  status: Maybe<string>,
): Maybe<ResaleType> {
  const collectionType = TAB_COLLECTION_RESALE_TYPE[tab];
  const currentType = customerItemStatusToCurrentResaleType(tab, status);
  if (!currentType) return null;
  const collectionRank = resaleTypeRank(collectionType);
  const currentRank = resaleTypeRank(currentType);
  if (collectionRank === currentRank) return null;
  return internalToClientResaleType(collectionType);
}
export const CustomerItemStatusToResaleStatus = {
  [CustomerItemStatus.PAID]: ItemStatus.PAID,
  [CustomerItemStatus.SOLD]: ItemStatus.SOLD,
  [CustomerItemStatus.PENDING_SALE]: ItemStatus.SOLD,
  [CustomerItemStatus.CLAIMED]: ItemStatus.CLAIMED,
  [CustomerItemStatus.DONATED]: ItemStatus.DONATED,
  [CustomerItemStatus.FREE]: ItemStatus.SOLD,
  [CustomerItemStatus.RECYCLED]: ItemStatus.RECYCLED,
  [CustomerItemStatus.RETURNED]: ItemStatus.RETURNED,
  [CustomerItemStatus.FOR_SALE]: ItemStatus.STORED,
  [CustomerItemStatus.PROCESSING]: ItemStatus.STORED,
  [CustomerItemStatus.AUCTIONING]: ItemStatus.AUCTIONING,
  [CustomerItemStatus.AUCTIONED]: ItemStatus.AUCTIONED,
} as const;
